// @flow
import _ from 'lodash';
import type { TridentCampaign, Node, CampaignResourceInfo, SubAction } from 'types/trident-campaigns';
import { actionTypeToTemplateType, countryIDToLocales } from 'data/trident-campaigns';

const isHedwigActionType = (type: string) => {
  return type in actionTypeToTemplateType;
};

// checkScenarioNode returns a warning object { data: { "meta" => list of warnings } }
const checkScenarioNode = (node: Node, campaign: TridentCampaign) => {
  const warnings = {
    data: {
      meta: [],
    },
  };
  // check scenario start/end time
  const scenarioStart = _.get(node, 'data.meta.startTime');
  const scenarioEnd = _.get(node, 'data.meta.endTime');
  if (Math.abs(scenarioStart.diff(campaign.startTime, 'seconds') > 0)) {
    warnings.data.meta.push('Start time is different from campaign start time');
  }
  if (Math.abs(scenarioEnd.diff(campaign.endTime, 'seconds') > 0)) {
    warnings.data.meta.push('End time is different from campaign end time');
  }
  return warnings;
};

// checkHedwigAction checks a hedwig comms sub action, and return a list of warning messages
const checkHedwigAction = (action: SubAction, campaign: TridentCampaign, resourceInfo: CampaignResourceInfo) => {
  const templateMap = resourceInfo.templateInfo;
  const templateID = _.get(action, 'meta.messagingPayload.templateID');
  const warnings = [];

  // check template locale
  const countryDefaultLocales = _.get(countryIDToLocales, campaign.countryID, ['en']);
  const params = _.get(action, 'meta.messagingPayload.params');
  if (!_.some(params, (__, locale) => { return _.includes(countryDefaultLocales, locale); })) {
    warnings.push(`Country's default locales ${countryDefaultLocales} are NOT configured`);
  }

  const template = _.get(templateMap, templateID);
  if (!template) {
    warnings.push(`Info of template ${templateID} is not found`);
    return warnings;
  }

  // check template category
  if (template.category === 'transaction') {
    warnings.push(`Template ${templateID} is in transaction category`);
  }
  return warnings;
};

// checkActionsNode returns a warning object { limits: [ list of warnings ], data: { subActionIndex => list of warnings on the sub action} }
const checkActionsNode = (node: Node, campaign: TridentCampaign, resourceInfo: CampaignResourceInfo) => {
  const warnings = {
    limits: [],
    data: {}, // a map of subActionIndex => list of warnings
  };

  let isTimeEventAction = false;
  _.map(node.data, (subAction, index) => {
    if (isHedwigActionType(subAction.type)) {
      const subActionWarnings = checkHedwigAction(subAction, campaign, resourceInfo);
      if (!_.isEmpty(subActionWarnings)) {
        warnings.data[`${index}`] = subActionWarnings;
      }
    }
    if (subAction.type.startsWith('bulk')) {
      isTimeEventAction = true;
    }
    // TODO: check reward inventory
  });

  // check limit if not time event
  if (!isTimeEventAction) {
    const limits = _.get(node, 'limits', []);
    if (_.isEmpty(limits) || _.every(limits, limit => { return limit.limit <= 0; })) {
      warnings.limits.push('No limit is set');
    }
  }

  return warnings;
};

const nodeCheckerMap = {
  scenarios: checkScenarioNode,
  actions: checkActionsNode,
};

// checkCampaign returns a warning object { overall: [ list of messages ], nodes: { nodeID => warning object for the node } }
export function checkCampaign(campaign: TridentCampaign, resourceInfo: CampaignResourceInfo) {
  const warnings = {
    overall: [],
    nodes: {}, // a map nodeKey -> warning object
  };

  // check campaign duration
  const durationInDays = campaign.endTime.diff(campaign.startTime, 'days');
  if (durationInDays < 1) {
    warnings.overall.push('Campaign duration is less than one day');
  } else if (durationInDays > 180) {
    warnings.overall.push('Campaign duration is more than half a year');
  }

  // check nodes
  _.map(campaign.nodes, (node, key) => {
    const checkFunc = _.get(nodeCheckerMap, node.type);
    if (checkFunc) {
      warnings.nodes[key] = checkFunc(node, campaign, resourceInfo);
    }
  });

  return warnings;
}
